import React from 'react';
import { motion } from 'framer-motion';

/**
 * BEA Logo Component
 * Black Entrepreneurship Alliance branded logo with signature bars
 * 
 * Colors inspired by BEA branding:
 * - Primary: Deep purple/violet (#6B21A8 - empowerment)
 * - Secondary: Warm orange (#F97316 - energy, creativity)
 * - Accent: Teal/cyan (#14B8A6 - growth, trust)
 * - Accent 2: Pink (#EC4899 - bold, innovative)
 */

export const BEALogo = ({ 
  size = "md", 
  showText = true, 
  variant = "full",
  animated = false,
  className = "" 
}) => {
  const sizes = {
    xs: { bars: 16, text: "text-sm", gap: 1 },
    sm: { bars: 24, text: "text-lg", gap: 1.5 },
    md: { bars: 32, text: "text-xl", gap: 2 },
    lg: { bars: 48, text: "text-2xl", gap: 2.5 },
    xl: { bars: 64, text: "text-3xl", gap: 3 },
  };

  const config = sizes[size] || sizes.md;
  
  // BEA signature bars - representing data-driven impact
  const bars = [
    { color: "#6B21A8", height: "60%" },   // Purple - empowerment
    { color: "#F97316", height: "100%" },  // Orange - energy
    { color: "#14B8A6", height: "80%" },   // Teal - growth
    { color: "#EC4899", height: "45%" },   // Pink - innovation
  ];

  const LogoBars = () => (
    <div 
      className="flex items-end"
      style={{ 
        height: config.bars, 
        gap: config.gap,
      }}
    >
      {bars.map((bar, index) => (
        <motion.div
          key={index}
          className="rounded-sm"
          style={{
            width: config.bars / 5,
            height: bar.height,
            backgroundColor: bar.color,
          }}
          initial={animated ? { height: 0, opacity: 0 } : false}
          animate={animated ? { 
            height: bar.height, 
            opacity: 1,
          } : false}
          transition={{
            duration: 0.5,
            delay: index * 0.1,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );

  if (variant === "icon") {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <LogoBars />
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <LogoBars />
      {showText && (
        <motion.div 
          className="flex flex-col"
          initial={animated ? { opacity: 0, x: -10 } : false}
          animate={animated ? { opacity: 1, x: 0 } : false}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <span className={`font-bold text-white leading-none ${config.text}`}>
            BEA
          </span>
          <span className="text-white/60 text-[10px] font-medium tracking-wider uppercase">
            Co-Founder Connect
          </span>
        </motion.div>
      )}
    </div>
  );
};

// Compact logo for nav/header
export const BEALogoCompact = ({ className = "" }) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="flex items-end h-6 gap-0.5">
      <div className="w-1.5 h-[60%] rounded-sm bg-[#6B21A8]" />
      <div className="w-1.5 h-full rounded-sm bg-[#F97316]" />
      <div className="w-1.5 h-[80%] rounded-sm bg-[#14B8A6]" />
      <div className="w-1.5 h-[45%] rounded-sm bg-[#EC4899]" />
    </div>
    <span className="font-bold text-white">BEA</span>
  </div>
);

// Logo mark only (for favicon, app icon)
export const BEALogoMark = ({ size = 32, className = "" }) => (
  <div 
    className={`flex items-end ${className}`}
    style={{ height: size, gap: size / 16 }}
  >
    <div className="rounded-sm bg-[#6B21A8]" style={{ width: size / 5, height: '60%' }} />
    <div className="rounded-sm bg-[#F97316]" style={{ width: size / 5, height: '100%' }} />
    <div className="rounded-sm bg-[#14B8A6]" style={{ width: size / 5, height: '80%' }} />
    <div className="rounded-sm bg-[#EC4899]" style={{ width: size / 5, height: '45%' }} />
  </div>
);

export default BEALogo;
